<script setup>
import CaretDownIcon from '@iconify-vue/ph/caret-down';
import CaretLeftIcon from '@iconify-vue/ph/caret-left';
import CaretRightIcon from '@iconify-vue/ph/caret-right';
import { getLocalTimeZone, today } from '@internationalized/date';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import { Motion } from 'motion-v';
import {
    MonthPickerCell,
    MonthPickerCellTrigger,
    MonthPickerGrid,
    MonthPickerGridBody,
    MonthPickerGridRow,
    MonthPickerHeader,
    MonthPickerHeading,
    MonthPickerNext,
    MonthPickerPrev,
    MonthPickerRoot,
    PopoverContent,
    PopoverPortal,
    PopoverRoot,
    PopoverTrigger,
} from 'reka-ui';
import { computed, ref, watch } from 'vue';

const model = defineModel();

const {
    size = 'medium',
    type = 'primary',
    maxValue = undefined,
    minValue = undefined,
    asList = false,
    hasEmptyOption = false,
} = defineProps({
    size: {
        type: String,
        required: false,
    },
    type: {
        type: String,
        required: false,
    },
    maxValue: {
        type: Object,
        required: false,
    },
    minValue: {
        type: Object,
        required: false,
    },
    asList: {
        type: Boolean,
        required: false,
    },
    hasEmptyOption: {
        type: Boolean,
        required: false,
    },
});

const MONTHS = [
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre',
];

const isOpen = ref(false);

const draft = ref(model.value ?? undefined);

const placeholder = ref(
    model.value ?? maxValue ?? today(getLocalTimeZone()).set({ day: 1 }),
);

watch(isOpen, (open) => {
    if (open) {
        draft.value = model.value ?? undefined;

        if (draft.value) {
            placeholder.value = draft.value;
        }
    }
});

function formatMonth(date) {
    return `${MONTHS[date.month - 1]} ${date.year}`;
}

const hasValue = computed(() => Boolean(model.value));

const label = computed(() => (hasValue.value ? formatMonth(model.value) : ''));

const availableMonths = computed(() => {
    if (!minValue || !maxValue) {
        return model.value ? [model.value] : [];
    }

    const months = [];
    const end = maxValue.set({ day: 1 });
    let cursor = minValue.set({ day: 1 });

    while (cursor.compare(end) <= 0) {
        months.push(cursor);
        cursor = cursor.add({ months: 1 });
    }

    return months;
});

function update() {
    if (draft.value) {
        model.value = draft.value;
    }

    isOpen.value = false;
}

function selectMonth(month) {
    model.value = month;
    isOpen.value = false;
}

function clear() {
    model.value = null;
    isOpen.value = false;
}
</script>

<template>
    <PopoverRoot v-model:open="isOpen">
        <PopoverTrigger as-child>
            <slot
                name="trigger"
                :label="label"
                :has-value="hasValue"
                :is-open="isOpen"
            >
                <ActionButton
                    :callback="() => {}"
                    :size="size"
                    :type="type"
                    :right-icon="CaretDownIcon"
                    :rotate-icon="isOpen"
                >
                    {{ label }}
                </ActionButton>
            </slot>
        </PopoverTrigger>

        <PopoverPortal>
            <PopoverContent align="start" :side-offset="6" class="z-50">
                <Motion
                    :initial="{ opacity: 0, scale: 0.96, y: -4 }"
                    :animate="{ opacity: 1, scale: 1, y: 0 }"
                    :transition="{ duration: 0.15 }"
                    class="overflow-hidden rounded-sm border border-gray-300 bg-white shadow-sm"
                    :style="{
                        minWidth: 'var(--reka-popover-trigger-width)',
                        width: asList
                            ? 'var(--reka-popover-trigger-width)'
                            : '',
                    }"
                    :class="{ 'p-4': !asList }"
                >
                    <div v-if="asList" class="flex flex-col">
                        <button
                            v-for="month in availableMonths"
                            :key="month.toString()"
                            type="button"
                            class="w-full cursor-pointer text-left text-sm capitalize duration-150"
                            :class="{
                                'bg-indigo-600 text-white':
                                    hasValue && month.compare(model) === 0,
                                'hover:bg-gray-100':
                                    !hasValue || month.compare(model) !== 0,
                                'px-5 py-2': size === 'medium',
                                'px-3 py-1': size === 'small',
                            }"
                            @click="selectMonth(month)"
                        >
                            {{ formatMonth(month) }}
                        </button>
                    </div>

                    <MonthPickerRoot
                        v-else
                        v-slot="{ grid }"
                        v-model="draft"
                        v-model:placeholder="placeholder"
                        :max-value="maxValue"
                        :min-value="minValue"
                        locale="fr"
                        class="select-none"
                    >
                        <MonthPickerHeader
                            class="mb-4 flex items-center justify-between"
                        >
                            <MonthPickerPrev
                                class="rounded-sm p-1 duration-150 hover:bg-[#eff2f5] data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40 data-[disabled]:hover:bg-transparent"
                            >
                                <CaretLeftIcon class="size-5" />
                            </MonthPickerPrev>

                            <MonthPickerHeading class="font-medium" />

                            <MonthPickerNext
                                class="rounded-sm p-1 duration-150 hover:bg-[#eff2f5] data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40 data-[disabled]:hover:bg-transparent"
                            >
                                <CaretRightIcon class="size-5" />
                            </MonthPickerNext>
                        </MonthPickerHeader>

                        <MonthPickerGrid class="border-collapse">
                            <MonthPickerGridBody class="grid gap-y-1">
                                <MonthPickerGridRow
                                    v-for="(months, index) in grid.rows"
                                    :key="`month-${index}`"
                                    class="grid grid-cols-4 gap-x-1"
                                >
                                    <MonthPickerCell
                                        v-for="month in months"
                                        :key="month.toString()"
                                        :date="month"
                                        class="text-center"
                                    >
                                        <MonthPickerCellTrigger
                                            :month="month"
                                            class="flex w-full cursor-pointer items-center justify-center rounded-sm px-3 py-2 text-sm capitalize duration-150 outline-none hover:bg-[#eff2f5] data-[disabled]:cursor-not-allowed data-[disabled]:text-gray-400 data-[disabled]:hover:bg-transparent data-[selected]:bg-[#4d65b9] data-[selected]:text-white data-[selected]:hover:bg-[#4d65b9]"
                                        />
                                    </MonthPickerCell>
                                </MonthPickerGridRow>
                            </MonthPickerGridBody>
                        </MonthPickerGrid>
                    </MonthPickerRoot>

                    <ActionButton
                        v-if="!asList"
                        :callback="update"
                        class="mt-4 w-full"
                        size="small"
                    >
                        Mettre à jour
                    </ActionButton>

                    <ActionButton
                        v-if="!asList && hasEmptyOption && hasValue"
                        :callback="clear"
                        class="mt-2 w-full"
                        size="small"
                        type="secondary"
                    >
                        Effacer
                    </ActionButton>
                </Motion>
            </PopoverContent>
        </PopoverPortal>
    </PopoverRoot>
</template>
